// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import type {Channel} from '@app/features/channel/models/Channel';
import Guilds from '@app/features/guild/state/Guilds';
import GuildMembers from '@app/features/member/state/GuildMembers';
import Permission from '@app/features/permissions/state/Permission';
import {
	type AutoModConfig,
	type AutoModEvaluationResult,
	type AutoModRule,
	DEFAULT_AUTOMOD_RULES,
} from '@app/features/automod/types/AutoModTypes';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import {makeAutoObservable} from 'mobx';

const STORAGE_PREFIX = 'fluxer:automod:';

function testKeywordMatch(content: string, keyword: string): boolean {
	const trimmed = keyword.trim();
	if (!trimmed) return false;

	// Check regex format: /pattern/flags
	if (trimmed.startsWith('/') && trimmed.lastIndexOf('/') > 0) {
		const lastSlash = trimmed.lastIndexOf('/');
		const pattern = trimmed.slice(1, lastSlash);
		const flags = trimmed.slice(lastSlash + 1);
		try {
			const regex = new RegExp(pattern, flags || 'i');
			return regex.test(content);
		} catch {
			// Fallback to substring
		}
	}

	// Wildcard pattern: *foo* or foo* or *foo
	if (trimmed.includes('*')) {
		const escaped = trimmed
			.split('*')
			.map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
			.join('.*');
		const regex = new RegExp(escaped, 'i');
		return regex.test(content);
	}

	// Boundary-aware word match (or substring for non-alphanumeric)
	const isAlphaNumeric = /^[a-zA-Z0-9_\-]+$/.test(trimmed);
	if (isAlphaNumeric) {
		const regex = new RegExp(`\\b${trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
		return regex.test(content);
	}

	return content.toLowerCase().includes(trimmed.toLowerCase());
}

function countMentions(content: string): number {
	let count = 0;
	// User mentions <@123> or <@!123>
	const userMentions = content.match(/<@!?\d+>/g);
	if (userMentions) count += userMentions.length;

	// Role mentions <@&123>
	const roleMentions = content.match(/<@&\d+>/g);
	if (roleMentions) count += roleMentions.length;

	// Everyone and here
	const everyoneMentions = content.match(/@(everyone|here)\b/g);
	if (everyoneMentions) count += everyoneMentions.length;

	return count;
}

class AutoModStore {
	private configs = new Map<string, AutoModConfig>();

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	getConfig(guildId: string): AutoModConfig {
		if (this.configs.has(guildId)) {
			return this.configs.get(guildId)!;
		}

		// Try loading from localStorage
		let config: AutoModConfig | null = null;
		if (typeof window !== 'undefined') {
			try {
				const saved = localStorage.getItem(`${STORAGE_PREFIX}${guildId}`);
				if (saved) {
					config = JSON.parse(saved);
				}
			} catch {
				// Ignore parse error
			}
		}

		if (!config || !config.rules || config.rules.length === 0) {
			config = {
				guildId,
				rules: DEFAULT_AUTOMOD_RULES.map((rule, idx) => ({
					...rule,
					id: `rule-${idx + 1}-${Date.now()}`,
				})),
				logChannelId: null,
			};
		}

		this.configs.set(guildId, config);
		return config;
	}

	private saveConfig(guildId: string, config: AutoModConfig): void {
		this.configs.set(guildId, config);
		if (typeof window !== 'undefined') {
			try {
				localStorage.setItem(`${STORAGE_PREFIX}${guildId}`, JSON.stringify(config));
			} catch {
				// Local storage full or unavailable
			}
		}
	}

	updateRule(guildId: string, rule: AutoModRule): void {
		const current = this.getConfig(guildId);
		const rules = current.rules.map((r) => (r.id === rule.id ? {...rule} : r));
		this.saveConfig(guildId, {...current, rules});
	}

	addRule(guildId: string, rule: Omit<AutoModRule, 'id'>): AutoModRule {
		const current = this.getConfig(guildId);
		const newRule: AutoModRule = {
			...rule,
			id: `rule-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
		};
		const rules = [...current.rules, newRule];
		this.saveConfig(guildId, {...current, rules});
		return newRule;
	}

	deleteRule(guildId: string, ruleId: string): void {
		const current = this.getConfig(guildId);
		const rules = current.rules.filter((r) => r.id !== ruleId);
		this.saveConfig(guildId, {...current, rules});
	}

	setLogChannel(guildId: string, channelId: string | null): void {
		const current = this.getConfig(guildId);
		this.saveConfig(guildId, {...current, logChannelId: channelId});
	}

	evaluateMessage(channel: Channel, content: string): AutoModEvaluationResult {
		if (!channel.guildId) {
			return {blocked: false};
		}

		const guildId = channel.guildId;
		const currentUserId = Authentication.currentUserId;
		if (!currentUserId) return {blocked: false};

		// Check bypass permissions
		const guild = Guilds.getGuild(guildId);
		if (guild?.ownerId === currentUserId) {
			return {blocked: false};
		}

		const canBypass =
			Permission.can(Permissions.ADMINISTRATOR, channel) ||
			Permission.can(Permissions.MANAGE_GUILD, channel) ||
			Permission.can(Permissions.MANAGE_MESSAGES, channel);
		if (canBypass) {
			return {blocked: false};
		}

		// Check member roles against exempt roles
		const member = GuildMembers.getMember(guildId, currentUserId);
		const userRoles = member?.roles;
		const hasExemptRole = (roleId: string): boolean => {
			if (!userRoles) return false;
			if (userRoles instanceof Set) return userRoles.has(roleId);
			if (Array.isArray(userRoles)) return (userRoles as Array<string>).includes(roleId);
			return false;
		};

		const config = this.getConfig(guildId);

		for (const rule of config.rules) {
			if (!rule.enabled) continue;

			// Check channel exemption
			if (rule.exemptChannelIds?.includes(channel.id)) continue;

			// Check role exemption
			if (rule.exemptRoleIds?.some(hasExemptRole)) continue;

			// 1. Keyword check
			if (rule.triggerType === 'KEYWORD') {
				for (const keyword of rule.keywords) {
					if (testKeywordMatch(content, keyword)) {
						return {
							blocked: rule.action === 'BLOCK_MESSAGE',
							reason: `Triggered prohibited word rule "${rule.name}"`,
							ruleName: rule.name,
							matchedPattern: keyword,
						};
					}
				}
			}

			// 2. Mention spam check
			if (rule.triggerType === 'MENTION_SPAM') {
				const mentions = countMentions(content);
				if (rule.mentionLimit > 0 && mentions > rule.mentionLimit) {
					return {
						blocked: rule.action === 'BLOCK_MESSAGE',
						reason: `Too many mentions (${mentions} mentions exceed limit of ${rule.mentionLimit})`,
						ruleName: rule.name,
					};
				}
			}

			// 3. Spam / Phishing link check
			if (rule.triggerType === 'SPAM_LINK') {
				for (const pattern of rule.keywords) {
					if (content.toLowerCase().includes(pattern.toLowerCase())) {
						return {
							blocked: rule.action === 'BLOCK_MESSAGE',
							reason: `Triggered anti-phishing rule "${rule.name}"`,
							ruleName: rule.name,
							matchedPattern: pattern,
						};
					}
				}
			}
		}

		return {blocked: false};
	}

	testMessage(guildId: string, content: string): AutoModEvaluationResult {
		const config = this.getConfig(guildId);

		for (const rule of config.rules) {
			if (!rule.enabled) continue;

			if (rule.triggerType === 'KEYWORD') {
				for (const keyword of rule.keywords) {
					if (testKeywordMatch(content, keyword)) {
						return {
							blocked: true,
							reason: `Matches keyword rule "${rule.name}"`,
							ruleName: rule.name,
							matchedPattern: keyword,
						};
					}
				}
			}

			if (rule.triggerType === 'MENTION_SPAM') {
				const mentions = countMentions(content);
				if (rule.mentionLimit > 0 && mentions > rule.mentionLimit) {
					return {
						blocked: true,
						reason: `Exceeds mention limit (${mentions}/${rule.mentionLimit})`,
						ruleName: rule.name,
					};
				}
			}

			if (rule.triggerType === 'SPAM_LINK') {
				for (const pattern of rule.keywords) {
					if (content.toLowerCase().includes(pattern.toLowerCase())) {
						return {
							blocked: true,
							reason: `Matches known scam link pattern "${pattern}"`,
							ruleName: rule.name,
							matchedPattern: pattern,
						};
					}
				}
			}
		}

		return {blocked: false};
	}
}

const instance = new AutoModStore();
export default instance;
